import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';

import { Button, CircularProgress } from '@mui/material';

import type { UseCase } from 'api/types';
import { getThreadMessages, streamChat } from 'entities/chat/api/chatClient';
import { useLatestThreadPage } from 'entities/chat/api/useLatestThreadPage';
import { useOnboardingSetup } from 'entities/onboarding/api/useOnboardingSetup';
import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { ChatComposer } from 'features/chat/ui/ChatComposer';
import { SupportTicketButton } from 'features/chat/ui/SupportTicketButton';
import type { Prefill } from 'features/onboarding/lib/conversation';
import {
  conversationReducer,
  historyToMessages,
  initialConversation,
  serializeConversation,
} from 'features/onboarding/lib/conversation';
import { deriveClientType } from 'features/onboarding/lib/harmonize';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { ChatThread } from 'features/onboarding/ui/ChatThread';
import { useGetWidgetSettings } from 'hooks/customization';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useSandboxContext } from 'shared/lib/sandbox/useSandboxContext';

// px from the top that triggers loading the previous page of messages.
const LOAD_OLDER_THRESHOLD = 48;
// px from the bottom within which we keep the view pinned to the latest message.
const STICK_TO_BOTTOM_THRESHOLD = 80;

type ConversationProps = {
  wizardComplete: boolean;
  checklistEnabled: boolean;
  /** False for a teammate: the vertical is company config only an owner may set. */
  canSetVertical: boolean;
  prefill: Prefill;
  history: ReturnType<typeof historyToMessages>;
  threadId: string | null;
  oldestId: string | null;
  hasMore: boolean;
  /** Rendered over the thread, leaving the composer reachable. */
  overlay?: ReactNode;
  /** Told when the user asks something, so whatever covers the thread can move aside. */
  onAsk?: () => void;
  /** Positions itself against the composer row; the hide control straddles the panel's edge. */
  leadingAction?: ReactNode;
};

function Conversation({
  wizardComplete,
  checklistEnabled,
  canSetVertical,
  prefill,
  history,
  threadId,
  oldestId,
  hasMore,
  overlay,
  onAsk,
  leadingAction,
}: ConversationProps) {
  const [state, dispatch] = useReducer(conversationReducer, null, () =>
    initialConversation({ wizardComplete, prefill, history, checklistEnabled, canSetVertical }),
  );
  const { mutateAsync: submitSetup } = useOnboardingSetup();
  const { isSandboxOn } = useSandboxContext();
  // Kept in the store, not here: closing the panel unmounts this component.
  const draft = useOnboardingDialogStore((state) => state.draft);
  const setDraft = useOnboardingDialogStore((state) => state.setDraft);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  // Thread carries multi-turn context (resumed from history); abort cancels an in-flight stream.
  const threadIdRef = useRef<string | null>(threadId);
  const abortRef = useRef<AbortController | null>(null);

  // Reverse-infinite-scroll: cursor is the oldest message we hold; anchoring keeps the viewport
  // steady when older messages are prepended.
  const [cursor, setCursor] = useState({ oldestId, hasMore });
  const loadingOlderRef = useRef(false);
  const prependingRef = useRef(false);
  const beforePrependRef = useRef({ height: 0, top: 0 });
  // Pin to the bottom on open and as content streams/loads, until the user scrolls up.
  const stickToBottomRef = useRef(true);

  const isStreaming = state.messages.some((message) => message.kind === 'text' && message.streaming);

  // Submit the wizard answers once, when the conversation enters the submitting phase.
  useEffect(() => {
    if (state.phase !== 'submitting' || !state.clientType) {
      return;
    }
    let cancelled = false;
    submitSetup({ client_type: state.clientType, verticals: state.verticals as UseCase[] })
      .then(() => !cancelled && dispatch({ type: 'SUBMIT_DONE' }))
      .catch(() => !cancelled && dispatch({ type: 'SUBMIT_ERROR' }));
    return () => {
      cancelled = true;
    };
  }, [state.phase, state.clientType, state.verticals, submitSetup]);

  // Keep the view pinned to the bottom on new/streamed messages, but hold position when older
  // messages are prepended (otherwise loading history would yank the user to the bottom).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run whenever the message list changes
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    if (prependingRef.current) {
      el.scrollTop = beforePrependRef.current.top + (el.scrollHeight - beforePrependRef.current.height);
      prependingRef.current = false;
    } else if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [state.messages]);

  // Images and markdown load after mount, growing the content; keep pinned to the bottom while the
  // user hasn't scrolled up, so opening the assistant lands at the very bottom.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!(el && content)) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (!prependingRef.current && stickToBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !cursor.hasMore || !cursor.oldestId || !threadIdRef.current) {
      return;
    }
    loadingOlderRef.current = true;
    const el = scrollRef.current;
    if (el) {
      beforePrependRef.current = { height: el.scrollHeight, top: el.scrollTop };
    }
    try {
      const page = await getThreadMessages({ threadId: threadIdRef.current, beforeId: cursor.oldestId });
      const older = historyToMessages(page.messages);
      if (older.length) {
        prependingRef.current = true;
        dispatch({ type: 'LOAD_OLDER', messages: older });
      }
      setCursor({ oldestId: page.messages[0]?.id ?? cursor.oldestId, hasMore: page.hasMore });
    } catch {
      // getThreadMessages resolves to an empty page on error; stop offering "load older" so a
      // persistent failure doesn't retry on every scroll.
      setCursor((current) => ({ ...current, hasMore: false }));
    } finally {
      loadingOlderRef.current = false;
    }
  }, [cursor.hasMore, cursor.oldestId]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_THRESHOLD;
    if (el.scrollTop <= LOAD_OLDER_THRESHOLD) {
      void loadOlder();
    }
  }, [loadOlder]);

  const askAssistant = useCallback(
    (text: string) => {
      // The checklist covers the thread, so an answer would stream in unseen behind it.
      onAsk?.();
      // Asking is a deliberate return to the conversation, so follow it even if the user had
      // scrolled up to read something older — otherwise the answer streams in off-screen.
      stickToBottomRef.current = true;
      dispatch({ type: 'ASK_AI', text });
      const controller = new AbortController();
      abortRef.current = controller;
      // Where the user is, so the assistant + docs lookup can tailor the answer to the current page.
      const context = { path: window.location.pathname, sandbox: isSandboxOn };
      void streamChat(
        text,
        (event) => {
          if (event.type === 'thread') {
            threadIdRef.current = event.threadId;
          } else if (event.type === 'text-delta') {
            dispatch({ type: 'AI_DELTA', text: event.text });
          } else if (event.type === 'confirmation') {
            dispatch({
              type: 'SHOW_ORDER_PREVIEW',
              preview: {
                previewToken: event.previewToken,
                env: event.env,
                draft: event.draft,
                threadId: threadIdRef.current,
              },
            });
          } else if (event.type === 'finish' || event.type === 'abort') {
            dispatch({ type: 'AI_DONE' });
          } else if (event.type === 'error') {
            dispatch({ type: 'AI_ERROR', message: event.message });
          }
        },
        controller.signal,
        threadIdRef.current,
        context,
        // streamChat settles via events on failure; this guards against an unexpected rejection so
        // the streaming placeholder can't hang.
      ).catch(() => dispatch({ type: 'AI_ERROR', message: 'Something went wrong handling your request.' }));
    },
    [isSandboxOn, onAsk],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Clipped so the checklist slides out of sight under the header instead of over it. */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div ref={scrollRef} onScroll={handleScroll} className="absolute inset-0 overflow-y-auto px-4 py-4">
          <div ref={contentRef} className="mx-auto flex w-full max-w-2xl flex-col gap-4">
            {cursor.hasMore && (
              <div className="flex justify-center py-1">
                <CircularProgress size={16} />
              </div>
            )}
            <ChatThread
              state={state}
              onToggleVertical={(id) => dispatch({ type: 'TOGGLE_VERTICAL', id })}
              onConfirmVerticals={() => dispatch({ type: 'CONFIRM_VERTICALS' })}
            />
          </div>
        </div>
        {overlay}
      </div>
      {/* Relative so the leading action can hang off the panel's edge. */}
      {/* Extra room on the left so the field clears the hide control on the panel's edge. */}
      <div className="relative flex-none border-grey30 border-t bg-white pt-3 pr-4 pb-4 pl-7">
        {leadingAction}
        <div className="mx-auto w-full max-w-2xl">
          <ChatComposer
            isStreaming={isStreaming}
            draft={draft}
            onSend={askAssistant}
            onStop={() => abortRef.current?.abort()}
            onDraftChange={setDraft}
          />
          <div className="mt-1 flex justify-end">
            <SupportTicketButton getTranscript={() => serializeConversation(state.messages)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Loads onboarding state, what signup / the post-signup subscription wizard already captured, and the
// latest page of the resumed chat thread, then runs the conversation.
export function OnboardingConversation({
  overlay,
  onAsk,
  leadingAction,
}: {
  overlay?: ReactNode;
  onAsk?: () => void;
  leadingAction?: ReactNode;
}) {
  const { data, isPending, isError, refetch } = useOnboardingState();
  const { user } = useAuth();
  // retry:false, so an error resolves quickly to "no use_case known" and we just ask the question.
  const { data: widgetSettings, isPending: isWidgetSettingsPending } = useGetWidgetSettings();
  // The reducer below seeds from `history` exactly once, so this has to hold until a page fetched
  // *by this mount* arrives: on reopen the query has a cached page from last time, and mounting off
  // that locks in a page predating everything sent this session. `isFetchedAfterMount` counts an
  // error as settled too, so a thread that won't load degrades to an empty one rather than spinning
  // forever — and unlike `isFetching` it stays true, so a later refetch (a reconnect, say) can't
  // unmount the conversation and take its messages and in-flight stream with it.
  const { data: threadPage, isFetchedAfterMount: isThreadSettled } = useLatestThreadPage();

  // Without this, an errored state query (isPending false, data undefined) would spin forever.
  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-grey80">We couldn't load your onboarding. Please try again.</p>
        <Button variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (isPending || !data || isWidgetSettingsPending || !isThreadSettled) {
    return (
      <div className="flex h-full items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  const useCase = widgetSettings?.use_case;
  const prefill: Prefill = {
    clientType: deriveClientType(user),
    verticals: useCase ? [useCase] : null,
  };
  const serverMessages = threadPage?.messages ?? [];
  return (
    <Conversation
      canSetVertical={Boolean(user?.is_owner)}
      wizardComplete={data.wizard_complete}
      // Absent on an older backend, which is the pre-enrollment behaviour: show the checklist.
      checklistEnabled={data.checklist_enabled !== false}
      prefill={prefill}
      history={historyToMessages(serverMessages)}
      threadId={threadPage?.threadId ?? null}
      oldestId={serverMessages[0]?.id ?? null}
      hasMore={threadPage?.hasMore ?? false}
      overlay={overlay}
      onAsk={onAsk}
      leadingAction={leadingAction}
    />
  );
}
